// src/pages/admin/ReportDetailPage.tsx
// ============================================================
// SALT — Admin Report Detail Page
//
// Shows full report details including:
// - Content snapshot (image or text)
// - Reporter info, reason, details
// - Related reports for the same content
// - Action buttons: Clear, Dismiss, Hide Content
//
// "Clear" marks all pending reports as 'cleared' so they
// don't count toward the 3-report auto-hide threshold.
//
// Uses the shared Modal component for action confirmations.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import Modal from '../../components/ui/Modal';

import '../../styles/admin-reports.css';

interface RelatedReport {
  reportId: number;
  reporterUsername: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
}

interface ReportDetail {
  reportId: number;
  reporterUsername: string;
  reporterId: number;
  contentType: string;
  targetId: number;
  targetName: string;
  reason: string;
  details: string | null;
  contentUrl: string | null;
  status: string;
  adminNote: string | null;
  reviewedBy: number | null;
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  hiddenAt: string | null;
  reportCount: number;
  createdAt: string;
  updatedAt: string;
  relatedReports: RelatedReport[];
}

type ReportAction = 'clear' | 'dismiss' | 'hide_content';

interface ActionConfig {
  title: string;
  message: string;
  confirmLabel: string;
}

const ACTION_CONFIGS: Record<ReportAction, ActionConfig> = {
  clear: {
    title: 'Clear Report',
    message: 'Clear all pending reports for this content? This resets the report count so future reports won\'t be affected by these.',
    confirmLabel: 'Clear Report',
  },
  dismiss: {
    title: 'Dismiss Report',
    message: 'Dismiss this report as invalid? Content will be unhidden if it was auto-hidden.',
    confirmLabel: 'Dismiss',
  },
  hide_content: {
    title: 'Hide Content',
    message: 'Hide this content from all users? This confirms the content violates platform rules.',
    confirmLabel: 'Hide Content',
  },
};

function formatContentType(ct: string): string {
  return ct.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending': return 'sl-report-badge--pending';
    case 'actioned': return 'sl-report-badge--actioned';
    case 'dismissed': return 'sl-report-badge--dismissed';
    case 'cleared': return 'sl-report-badge--cleared';
    default: return '';
  }
}

function isImageContent(contentType: string): boolean {
  return ['user_avatar', 'team_logo'].includes(contentType);
}

export default function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [adminNote, setAdminNote] = useState('');

  // Modal state
  const [modalAction, setModalAction] = useState<ReportAction | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosClient.get(`/admin/reports/${reportId}`);
      setReport(res.data.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const openActionModal = (action: ReportAction) => {
    setModalAction(action);
  };

  const closeActionModal = () => {
    setModalAction(null);
  };

  const handleConfirmAction = async () => {
    if (!report || !modalAction) return;

    setActionLoading(true);
    setError('');
    closeActionModal();

    try {
      await axiosClient.post(`/admin/reports/${report.reportId}/review`, {
        action: modalAction,
        adminNote: adminNote.trim() || undefined,
      });

      const actionLabels: Record<ReportAction, string> = {
        clear: 'Reports cleared successfully',
        dismiss: 'Report dismissed',
        hide_content: 'Content hidden',
      };
      setSuccessMessage(actionLabels[modalAction]);
      setAdminNote('');

      await fetchReport();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const navigateToTarget = () => {
    if (!report) return;
    if (['user_avatar', 'user_bio', 'ign'].includes(report.contentType)) {
      navigate(`/admin/users/${report.targetId}`);
    } else if (['team_logo', 'team_description', 'team_name'].includes(report.contentType)) {
      navigate(`/teams/${report.targetId}`);
    }
  };

  if (loading) {
    return (
      <div className="sl-admin-loading">
        <div className="sl-admin-loading-spinner" />
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="sl-admin-reports">
        <div className="sl-admin-error">{error}</div>
        <button className="sl-report-back-btn" onClick={() => navigate('/admin/reports')}>
          ← Back to Reports
        </button>
      </div>
    );
  }

  if (!report) return null;

  const isPending = report.status === 'pending';
  const totalReports = report.relatedReports.length + 1;
  const currentConfig = modalAction ? ACTION_CONFIGS[modalAction] : null;

  return (
    <div className="sl-admin-reports">
      {/* Action Confirmation Modal */}
      <Modal
        isOpen={modalAction !== null}
        title={currentConfig?.title}
        onConfirm={handleConfirmAction}
        onCancel={closeActionModal}
        confirmLabel={currentConfig?.confirmLabel || 'Confirm'}
        cancelLabel="Cancel"
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-report-modal-message">{currentConfig?.message}</p>
        {adminNote.trim() && (
          <div className="sl-report-modal-note">
            <span className="sl-report-modal-note-label">Your note:</span>
            <span className="sl-report-modal-note-text">{adminNote}</span>
          </div>
        )}
      </Modal>

      {/* Header */}
      <div className="sl-report-detail-header">
        <button className="sl-report-back-btn" onClick={() => navigate('/admin/reports')}>
          ← Back
        </button>
        <div>
          <h1 className="sl-admin-page-title">Report #{report.reportId}</h1>
          <p className="sl-admin-page-subtitle">
            {formatContentType(report.contentType)} — {report.targetName}
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="sl-report-success-banner">{successMessage}</div>
      )}
      {error && <div className="sl-admin-error sl-admin-error--inline">{error}</div>}

      <div className="sl-report-detail-grid">
        {/* Content Preview Card */}
        <div className="sl-card sl-report-card">
          <h3 className="sl-admin-card-title">Reported Content</h3>
          <div className="sl-report-content-preview">
            {isImageContent(report.contentType) && report.contentUrl ? (
              <div className="sl-report-image-wrapper">
                <img
                  src={report.contentUrl}
                  alt="Reported content"
                  className="sl-report-image"
                />
              </div>
            ) : report.contentUrl ? (
              <div className="sl-report-text-preview">
                <p className="sl-report-text-content">{report.contentUrl}</p>
              </div>
            ) : (
              <div className="sl-report-no-content">No content snapshot available</div>
            )}
          </div>
          {report.hiddenAt && (
            <div className="sl-report-hidden-notice">
              Content was auto-hidden on {formatDate(report.hiddenAt)}
            </div>
          )}
        </div>

        {/* Report Info Card */}
        <div className="sl-card sl-report-card">
          <h3 className="sl-admin-card-title">Report Details</h3>
          <div className="sl-report-meta">
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Status</span>
              <span className={`sl-report-badge ${getStatusClass(report.status)}`}>
                {report.status}
              </span>
            </div>
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Target</span>
              <span className="sl-report-meta-value">
                <button
                  type="button"
                  className="sl-report-target-link"
                  onClick={navigateToTarget}
                >
                  {report.targetName}
                </button>
                <span className="sl-report-meta-dim">
                  ({formatContentType(report.contentType)}, ID: {report.targetId})
                </span>
              </span>
            </div>
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Reported By</span>
              <span className="sl-report-meta-value">{report.reporterUsername}</span>
            </div>
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Reason</span>
              <span className="sl-report-meta-value sl-report-reason-tag">{report.reason}</span>
            </div>
            {report.details && (
              <div className="sl-report-meta-row">
                <span className="sl-report-meta-label">Details</span>
                <span className="sl-report-meta-value">{report.details}</span>
              </div>
            )}
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Submitted</span>
              <span className="sl-report-meta-value">{formatDate(report.createdAt)}</span>
            </div>
            <div className="sl-report-meta-row">
              <span className="sl-report-meta-label">Pending Count</span>
              <span className="sl-report-meta-value">
                <span className={`sl-report-count-inline ${report.reportCount >= 3 ? 'sl-report-count-inline--danger' : ''}`}>
                  {report.reportCount}
                </span>
                <span className="sl-report-meta-dim"> of 3 needed for auto-hide</span>
              </span>
            </div>
            {report.reviewedByUsername && (
              <>
                <div className="sl-report-meta-row">
                  <span className="sl-report-meta-label">Reviewed By</span>
                  <span className="sl-report-meta-value">{report.reviewedByUsername}</span>
                </div>
                <div className="sl-report-meta-row">
                  <span className="sl-report-meta-label">Reviewed At</span>
                  <span className="sl-report-meta-value">{formatDate(report.reviewedAt)}</span>
                </div>
              </>
            )}
            {report.adminNote && (
              <div className="sl-report-meta-row">
                <span className="sl-report-meta-label">Admin Note</span>
                <span className="sl-report-meta-value">{report.adminNote}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions Card (only for pending reports) */}
        {isPending && (
          <div className="sl-card sl-report-card">
            <h3 className="sl-admin-card-title">Actions</h3>

            <div className="sl-report-note-group">
              <label className="sl-report-note-label">Admin Note (optional)</label>
              <textarea
                className="sl-report-note-textarea"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add a note about your decision..."
                rows={3}
              />
            </div>

            <div className="sl-report-actions">
              <button
                type="button"
                className="sl-report-action-btn sl-report-action-btn--clear"
                onClick={() => openActionModal('clear')}
                disabled={actionLoading}
              >
                Clear Report
                <span className="sl-report-action-desc">
                  Not severe — reset report count so future reports start fresh
                </span>
              </button>

              <button
                type="button"
                className="sl-report-action-btn sl-report-action-btn--dismiss"
                onClick={() => openActionModal('dismiss')}
                disabled={actionLoading}
              >
                Dismiss
                <span className="sl-report-action-desc">
                  Invalid report — unhides content if auto-hidden
                </span>
              </button>

              <button
                type="button"
                className="sl-report-action-btn sl-report-action-btn--hide"
                onClick={() => openActionModal('hide_content')}
                disabled={actionLoading}
              >
                Hide Content
                <span className="sl-report-action-desc">
                  Content violates rules — keep it hidden
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Resolved Banner */}
        {!isPending && (
          <div className={`sl-report-resolved-banner ${
            report.status === 'cleared' ? 'sl-report-resolved-banner--cleared' : ''
          }`}>
            {report.status === 'cleared' && 'This report was cleared — reports reset and content restored.'}
            {report.status === 'dismissed' && 'This report was dismissed.'}
            {report.status === 'actioned' && 'Action was taken on this report.'}
          </div>
        )}

        {/* Related Reports Card */}
        {report.relatedReports.length > 0 && (
          <div className="sl-card sl-report-card">
            <h3 className="sl-admin-card-title">
              Related Reports
              <span className="sl-report-related-count">
                {totalReports} total for this content
              </span>
            </h3>
            <div className="sl-report-related-list">
              {report.relatedReports.map((related) => (
                <div key={related.reportId} className="sl-report-related-item">
                  <div className="sl-report-related-top">
                    <span className="sl-report-related-id">
                      <button
                        type="button"
                        className="sl-report-target-link"
                        onClick={() => navigate(`/admin/reports/${related.reportId}`)}
                      >
                        #{related.reportId}
                      </button>
                    </span>
                    <span className="sl-report-related-reporter">{related.reporterUsername}</span>
                    <span className={`sl-report-badge sl-report-badge--sm ${getStatusClass(related.status)}`}>
                      {related.status}
                    </span>
                    <span className="sl-report-related-date">{formatDate(related.createdAt)}</span>
                  </div>
                  <div className="sl-report-related-bottom">
                    <span className="sl-report-reason-tag">{related.reason}</span>
                    {related.details && (
                      <span className="sl-report-related-details">{related.details}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}